import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, formatINR, formatDate, daysUntil } from "../lib/api";
import {
  TrendingUp, TrendingDown, Receipt, Package, AlertTriangle, Clock,
  Pill, ArrowRight, CreditCard,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar,
} from "recharts";

function Stat({ label, value, icon: Icon, accent = "blue", sub }) {
  const acc = {
    blue: "from-[#002FA7] to-[#1D4ED8]",
    green: "from-[#047857] to-[#10B981]",
    amber: "from-[#A16207] to-[#F59E0B]",
    red: "from-[#B91C1C] to-[#EF4444]",
  }[accent];
  return (
    <div className="stat-card" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-[10px]">{label}</div>
          <div className="mt-2 font-display text-3xl font-bold tracking-tight">{value}</div>
          {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${acc} text-white flex items-center justify-center`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    API.get("/dashboard").then((r) => setData(r.data)).catch((e) => setErr(e?.message || "Error"));
  }, []);

  if (err) return <div className="p-8 text-red-600">{err}</div>;
  if (!data) return <div className="p-8 text-slate-500">Loading dashboard…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Control Tower</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Good day. Here's your pharmacy.</h1>
          <p className="text-sm text-slate-500 mt-1">Live snapshot across stock, sales, and finances.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/sales" className="btn-primary" data-testid="quick-new-sale">
            <Receipt className="w-4 h-4" /> New Sale
          </Link>
          <Link to="/stock?add=1" className="btn-ghost" data-testid="quick-stock">
            <Package className="w-4 h-4" /> Add Stock
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Sales Today" value={formatINR(data.sales_today)} icon={TrendingUp} accent="blue" sub={`${data.bills_today} bills`} />
        <Stat label="Sales (Month)" value={formatINR(data.sales_month)} icon={Receipt} accent="green" sub={`Net ${formatINR(data.net_month)}`} />
        <Stat label="Expense (Month)" value={formatINR(data.expense_month)} icon={TrendingDown} accent="amber" />
        <Stat label="Alerts" value={`${data.low_stock_count + data.expiring_count}`} icon={AlertTriangle} accent="red"
              sub={`${data.low_stock_count} low · ${data.expiring_count} expiring`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline">Last 7 days</div>
              <h3 className="font-display text-lg font-semibold mt-1">Daily Sales</h3>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.chart_7d} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#002FA7" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#002FA7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} formatter={(v) => formatINR(v)} />
                <Area type="monotone" dataKey="total" stroke="#002FA7" strokeWidth={2} fill="url(#cg)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="overline">Best sellers · month</div>
          <h3 className="font-display text-lg font-semibold mt-1 mb-4">Top Medicines</h3>
          {data.top_medicines.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">No sales yet this month</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.top_medicines} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: "#0F172A" }} />
                  <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="qty" fill="#10B981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <AlertList
          title="Low Stock"
          subtitle="At or below threshold"
          icon={Package}
          items={data.low_stock}
          emptyLabel="All medicines well stocked ✓"
          renderMeta={(m) => <span className="tag tag-warning">Qty {m.quantity}</span>}
        />
        <AlertList
          title="Expiring Soon"
          subtitle="Within 60 days"
          icon={Clock}
          items={data.expiring}
          emptyLabel="No medicines expiring soon ✓"
          renderMeta={(m) => {
            const d = daysUntil(m.expiry_date);
            return <span className={`tag ${d < 30 ? "tag-danger" : "tag-warning"}`}>{d}d · {formatDate(m.expiry_date)}</span>;
          }}
        />
      </div>

      {data.outstanding_credit && data.outstanding_credit.count > 0 && (
        <div className="bg-white border border-amber-200 rounded-xl overflow-hidden" data-testid="outstanding-credit-card">
          <div className="p-5 border-b border-amber-100 flex items-center justify-between bg-amber-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#A16207] to-[#F59E0B] text-white flex items-center justify-center">
                <CreditCard className="w-5 h-5" />
              </div>
              <div>
                <div className="overline text-amber-800">Reminder</div>
                <h3 className="font-display text-lg font-semibold mt-0.5">
                  Outstanding Customer Credit · {formatINR(data.outstanding_credit.total)}
                </h3>
                <p className="text-xs text-slate-600 mt-0.5">{data.outstanding_credit.count} customer{data.outstanding_credit.count === 1 ? "" : "s"} owe money</p>
              </div>
            </div>
            <Link to="/customers" className="text-xs text-[#002FA7] font-semibold hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <ul className="divide-y divide-slate-100">
            {data.outstanding_credit.top.map((c) => (
              <li key={c.id} className="p-4 hover:bg-slate-50">
                <Link to={`/customer/${c.id}`} className="flex items-center gap-3" data-testid={`credit-customer-${c.id}`}>
                  <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-sm font-semibold">
                    {c.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{c.name}</div>
                    {c.phone && <div className="text-xs text-slate-500 truncate font-mono">{c.phone}</div>}
                  </div>
                  <span className="tag tag-warning">{formatINR(c.balance)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AlertList({ title, subtitle, icon: Icon, items, emptyLabel, renderMeta }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="p-5 border-b border-slate-200 flex items-center justify-between">
        <div>
          <div className="overline">{subtitle}</div>
          <h3 className="font-display text-lg font-semibold mt-1 flex items-center gap-2">
            <Icon className="w-4 h-4 text-slate-500" /> {title}
          </h3>
        </div>
        <Link to="/stock" className="text-xs text-[#002FA7] font-semibold hover:underline flex items-center gap-1">
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((m) => (
            <li key={m.id} className="p-4 hover:bg-slate-50">
              <Link to={`/medicine/${m.id}`} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center">
                  <Pill className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{m.name}</div>
                  <div className="text-xs text-slate-500 truncate">{m.company} · Batch {m.batch_number}</div>
                </div>
                {renderMeta(m)}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
